import type { ProgramUserRsvp } from 'models';
import { type ReactNode, useMemo } from 'react';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps, message } from 'ui';
import { isAfterJoinPeriod } from '../utils';
import { useEventRsvpLabels } from './useEventRsvpLabels';

type RsvpStatusDropdownOptionsResult =
  | {
      button: { icon: ReactNode; text: string };
      menu: MenuProps;
    }
  | undefined;

export const useRsvpStatusDropdownOptions = ({
  authorId,
  endTime,
  onGoing,
  onNotGoing,
  rsvp,
}: {
  authorId?: string;
  endTime: Date;
  onGoing: () => void;
  onNotGoing: () => void;
  rsvp?: ProgramUserRsvp;
}) => {
  const { getButtonActionLabel, getDropdownActionLabel } = useEventRsvpLabels();
  const { canDeclineEvent } = usePermissions();

  const dropdownOptions = useMemo((): RsvpStatusDropdownOptionsResult => {
    const isPastEvent = isAfterJoinPeriod(endTime);
    if (!rsvp || rsvp === 'pending' || isPastEvent) {
      return undefined;
    }

    switch (rsvp) {
      case 'going':
        return {
          button: {
            icon: <Icon color="success" icon="check-circle-fill" />,
            text: getDropdownActionLabel({ action: 'going' }),
          },
          menu: {
            items: [
              {
                icon: <Icon color="error" icon="close-circle-fill" />,
                key: 'reject',
                label: getButtonActionLabel({ action: 'not_going' }),
              },
            ],
            onClick(event) {
              event.domEvent.preventDefault();
              if (!canDeclineEvent({ authorId })) {
                message.error('You cannot decline an event where you are the author');
                return;
              }
              onNotGoing();
            },
          },
        };
      case 'not_going':
        return {
          button: {
            icon: <Icon color="error" icon="close-circle-fill" />,
            text: getDropdownActionLabel({ action: 'not_going' }),
          },
          menu: {
            items: [
              {
                icon: <Icon color="success" icon="check-circle-fill" />,
                key: 'accept',
                label: getButtonActionLabel({ action: 'going' }),
              },
            ],
            onClick(event) {
              event.domEvent.preventDefault();
              onGoing();
            },
          },
        };
    }
  }, [endTime, rsvp, getButtonActionLabel, getDropdownActionLabel, onNotGoing, onGoing, authorId, canDeclineEvent]);

  return { dropdownOptions };
};
